import React, { useState } from "react";
import { DayPicker } from "react-day-picker";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import * as P from "@/components/ui/popover";
import { usePrefs } from "@/contexts/PrefsContext";
import { todayInBaghdad } from "@/lib/time";

const Popover = P.Popover as any;
const PopoverTrigger = P.PopoverTrigger as any;
const PopoverContent = P.PopoverContent as any;

export const intlLoc = (lang: string) => (lang === "ar" ? "ar-IQ-u-nu-latn" : "en-GB");
const AR_DAYS = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const pad = (n: number) => String(n).padStart(2, "0");
export const toYmd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s?: string) => {
  if (!s) return undefined;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
};
const fmt = (d: Date, lang: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(intlLoc(lang), o).format(d);
const trigger = "h-10 w-full min-w-[9.5rem] rounded-full border bg-card px-4 text-sm inline-flex items-center gap-2 text-start disabled:opacity-50 hover:bg-accent/60 transition-[background-color]";

interface DateProps { value: string; onChange: (v: string) => void; min?: string; max?: string; testId: string; disabled?: boolean; clearable?: boolean; placeholder?: string; className?: string }

export const DateField = ({ value, onChange, min, max, testId, disabled, clearable, placeholder, className = "" }: DateProps) => {
  const { lang, t } = usePrefs();
  const [open, setOpen] = useState(false);
  const sel = parse(value);
  const lo = parse(min), hi = parse(max);
  const rtl = lang === "ar";
  const today = todayInBaghdad();
  const todayOk = (!min || today >= min) && (!max || today <= max);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={`relative ${className}`}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={testId} data-value={value || ""} className={`${trigger} ${clearable && value ? "pe-9" : ""}`}>
            <CalendarDays className="h-4 w-4 text-primary shrink-0" />
            <span className={`truncate ${sel ? "" : "text-muted-foreground"}`}>{sel ? fmt(sel, lang, { day: "numeric", month: "long", year: "numeric" }) : placeholder || t("dp.pick")}</span>
          </button>
        </PopoverTrigger>
        {clearable && value && !disabled && (
          <button type="button" onClick={() => onChange("")} aria-label={t("dp.clear")} data-testid={`${testId}-clear`} className="absolute end-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-full hover:bg-accent"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <PopoverContent align="start" className="w-auto p-2 rounded-3xl" data-testid={`${testId}-calendar`}>
        <DayPicker
          mode="single" dir={rtl ? "rtl" : "ltr"} weekStartsOn={6} selected={sel} defaultMonth={sel || lo}
          fromDate={lo} toDate={hi} today={parse(today)} showOutsideDays
          onSelect={(d) => { if (d) { onChange(toYmd(d)); setOpen(false); } }}
          formatters={{
            formatCaption: (d) => fmt(d, lang, { month: "long", year: "numeric" }),
            formatWeekdayName: (d) => (rtl ? AR_DAYS[d.getDay()] : fmt(d, lang, { weekday: "short" })),
            formatDay: (d) => String(d.getDate()),
          }}
          labels={{ labelPrevious: () => t("dp.prev"), labelNext: () => t("dp.next") }}
          classNames={{
            months: "flex flex-col", month: "space-y-3", caption: "flex justify-center pt-1 relative items-center h-9",
            caption_label: "text-sm font-bold", nav: "flex items-center",
            nav_button: "h-8 w-8 grid place-items-center rounded-full border hover:bg-accent disabled:opacity-30",
            nav_button_previous: "absolute start-1", nav_button_next: "absolute end-1",
            table: "w-full border-collapse", head_row: "flex", head_cell: "text-muted-foreground w-9 font-semibold text-[0.62rem] leading-tight",
            row: "flex w-full mt-1", cell: "p-0 text-center text-sm",
            day: "h-9 w-9 rounded-full font-semibold hover:bg-accent aria-selected:opacity-100 transition-[background-color]",
            day_selected: "!bg-primary !text-primary-foreground", day_today: "ring-1 ring-primary/50",
            day_outside: "text-muted-foreground/50", day_disabled: "text-muted-foreground opacity-30 pointer-events-none",
          }}
          components={{
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />,
          }}
        />
        <div className="flex justify-between px-2 pb-1 pt-1 text-xs font-bold">
          <button type="button" data-testid={`${testId}-today`} disabled={!todayOk} onClick={() => { onChange(today); setOpen(false); }} className="text-primary disabled:opacity-30">{t("dp.today")}</button>
          {clearable && <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-muted-foreground">{t("dp.clear")}</button>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const MonthField = ({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId: string }) => {
  const { lang } = usePrefs();
  const [open, setOpen] = useState(false);
  const [y, setY] = useState(Number(value.slice(0, 4)) || new Date().getFullYear());
  const cur = value ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, 1) : undefined;
  const rtl = lang === "ar";
  return (
    <Popover open={open} onOpenChange={(o: boolean) => { if (o) setY(Number(value.slice(0, 4)) || new Date().getFullYear()); setOpen(o); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} data-value={value} className={trigger}>
          <CalendarDays className="h-4 w-4 text-primary shrink-0" />{cur ? fmt(cur, lang, { month: "long", year: "numeric" }) : "—"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 rounded-3xl" data-testid={`${testId}-picker`}>
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={() => setY(y - 1)} data-testid={`${testId}-prev-year`} className="h-8 w-8 grid place-items-center rounded-full border">{rtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}</button>
          <span className="font-bold text-sm">{y}</span>
          <button type="button" onClick={() => setY(y + 1)} data-testid={`${testId}-next-year`} className="h-8 w-8 grid place-items-center rounded-full border">{rtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: 12 }, (_, i) => {
            const v = `${y}-${pad(i + 1)}`;
            return (
              <button key={v} type="button" data-testid={`${testId}-m-${i + 1}`} aria-pressed={v === value} onClick={() => { onChange(v); setOpen(false); }}
                className={`h-9 rounded-full text-xs font-bold ${v === value ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
                {fmt(new Date(y, i, 1), lang, { month: "short" })}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const selCls = "h-10 rounded-full border bg-card px-3 text-sm font-semibold";
// 12-hour picker (hour 1-12, minutes, AM/PM); value stays "HH:MM" 24h
const to24 = (h12: number, pm: boolean) => pad((h12 % 12) + (pm ? 12 : 0));

export const TimeField = ({ value, onChange, testId, clearable, max }: { value: string; onChange: (v: string) => void; testId: string; clearable?: boolean; max?: string }) => {
  const { t, lang } = usePrefs();
  const [h, m] = value ? value.split(":") : ["", ""];
  const hn = h ? Number(h) : NaN;
  const [pmPending, setPmPending] = React.useState(true);
  const pm = h ? hn >= 12 : pmPending;
  const mins = Array.from({ length: 12 }, (_, i) => pad(i * 5));
  if (m && !mins.includes(m)) mins.push(m), mins.sort();
  const set = (nh: string, nm: string) => onChange(nh ? `${nh}:${nm || "00"}` : "");
  const am = lang === "ar" ? "صباحاً" : "AM", pmL = lang === "ar" ? "مساءً" : "PM";
  return (
    <div dir="ltr" className="inline-flex items-center gap-1" data-testid={testId} data-value={value}>
      <select aria-label={t("dp.hour")} value={h ? String(hn % 12 || 12) : ""} data-testid={`${testId}-hour`} className={selCls}
        onChange={(e) => {
          if (!e.target.value) return set("", m);
          const nh = to24(Number(e.target.value), pm);
          set(nh, max && `${nh}:${m || "00"}` > max ? "00" : m);
        }}>
        {(clearable || !h) && <option value="">--</option>}
        {Array.from({ length: 12 }, (_, i) => i + 1).map((x) => <option key={x} value={x} disabled={!!max && `${to24(x, pm)}:00` > max}>{x}</option>)}
      </select>
      <span className="font-bold">:</span>
      <select aria-label={t("dp.minute")} value={m} disabled={!h} onChange={(e) => set(h, e.target.value)} data-testid={`${testId}-minute`} className={selCls}>
        {!m && <option value="">--</option>}
        {mins.map((x) => <option key={x} value={x} disabled={!!max && `${h}:${x}` > max}>{x}</option>)}
      </select>
      <select dir="auto" aria-label={t("dp.period")} value={pm ? "pm" : "am"} data-testid={`${testId}-period`} className={`${selCls} min-w-[6.5rem]`}
        onChange={(e) => {
          const isPm = e.target.value === "pm";
          const nh = h ? to24(hn % 12 || 12, isPm) : "";
          if (max && nh && `${nh}:${m || "00"}` > max) return;
          setPmPending(isPm);
          if (h) set(nh, m);
        }}>
        <option value="am">{am}</option>
        <option value="pm" disabled={!!max && (h ? `${to24(hn % 12 || 12, true)}:${m || "00"}` > max : max.slice(0, 2) < "12")}>{pmL}</option>
      </select>
    </div>
  );
};

export const DateTimeField = ({ value, onChange, testId, min, max }: { value: string; onChange: (v: string) => void; testId: string; min?: string; max?: string }) => {
  const [d, tm0] = value ? value.split("T") : ["", ""];
  const [pending, setPending] = useState("");
  const tm = tm0 || pending;
  const tMax = max && d && d === max.slice(0, 10) ? max.slice(11, 16) : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId} data-value={value}>
      <DateField value={d} min={min?.slice(0, 10)} max={max?.slice(0, 10)} onChange={(nd) => { const lim = max && nd === max.slice(0, 10) ? max.slice(11, 16) : ""; const nt = tm || "00:00"; onChange(nd ? `${nd}T${lim && nt > lim ? lim : nt}` : ""); }} testId={`${testId}-date`} className="flex-1" />
      <TimeField value={tm?.slice(0, 5) || ""} max={tMax} onChange={(nt) => (d ? onChange(`${d}T${nt || "00:00"}`) : setPending(nt))} testId={`${testId}-time`} />
    </div>
  );
};
